import React, { useCallback, useEffect, useState } from 'react'
import { shortenSido } from '../utils/address'
import { bookmarkApi, BookmarkCategory } from '../api/bookmark'
import { usePlaceBookmarkStore } from '../stores/placeBookmarkStore'
import { toastUtil } from '../utils/toast'
import { dialogUtil } from '../utils/dialog'
import { useNavigate } from 'react-router-dom'
import { rememberBeforeCreate } from '../utils/platelistCreateReturn'
import PlatelistPickerSheet from './PlatelistPickerSheet'

/** 저장을 해제하고 시트가 내려가기까지 (ms) */
const UNSAVE_CLOSE_MS = 500

interface PlaceSaveSheetProps {
  isOpen: boolean
  onClose: () => void
  /** 저장 대상 장소 */
  placeId: number
  placeName?: string
  /** 상단에 장소명 아래로 보여줄 주소 */
  placeAddress?: string
  /**
   * 지금 보고 있는 플레이트리스트 id. 이 리스트에서 빼면 화면의 행도 사라져야 하므로
   * onRemovedFromCurrentCategory로 알린다. (플레이트리스트 상세에서만 넘긴다)
   */
  currentCategoryId?: string
  onRemovedFromCurrentCategory?: () => void
  /**
   * 저장 상태가 바뀔 때 호출. isSaved는 "내가 저장한 장소인가"(= 북마크 아이콘 fill 여부).
   * 목록 화면이 아이콘을 갱신하거나 행을 지울 때 쓴다.
   */
  onSavedChange?: (isSaved: boolean) => void
  /**
   * 값이 바뀌면 목록을 다시 읽는다. 시트를 먼저 열고 저장 요청을 뒤로 보내는
   * 경우, 저장이 끝난 시점에 이 값을 올려 "저장 안 함"으로 보이는 걸 막는다.
   */
  reloadToken?: number
}

/**
 * 장소 한 곳의 저장 시트.
 *
 * 껍데기는 PlatelistPickerSheet 가 그린다 — 담기 시트와 생김새가 같아야 하므로
 * 행·목록·만들기 버튼은 거기 한 벌만 있다. 여기 있는 건 이 시트만의 두 층이다.
 *  - 머리말: "내가 이 장소를 저장했는가" (북마크 아이콘). 해제하면 내가 담은 모든
 *    리스트에서 빠진다. 남이 담은 행은 건드리지 않는다(removeSavedPlace 기준).
 *  - 목록: 플레이트리스트별로 "이 리스트에 있는가"(✓) / "없는가"(⊕).
 *    남이 먼저 담았어도 리스트에 존재하면 ✓다. 리스트 한 곳에서만 넣고 뺀다.
 *
 * 두 층의 기준이 다른 게 의도다. 머리말은 내 저장, 목록은 리스트 membership.
 */
const PlaceSaveSheet: React.FC<PlaceSaveSheetProps> = ({
  isOpen,
  onClose,
  placeId,
  placeName,
  placeAddress,
  currentCategoryId,
  onSavedChange,
  onRemovedFromCurrentCategory,
  reloadToken = 0,
}) => {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<BookmarkCategory[]>([])
  /** 이 장소가 들어있는 리스트 id */
  const [memberOf, setMemberOf] = useState<Set<string>>(new Set())
  /** 내가 저장했는가 (머리말 아이콘) */
  const [isSaved, setIsSaved] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  /** 연타로 같은 리스트에 중복 요청 나가는 것 방지 */
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())

  const placeIdString = String(placeId)

  /** 받아온 목록을 화면 상태로 옮긴다. 리스트 membership(✓)만 여기서 정한다. */
  const applyCategories = useCallback(
    (all: BookmarkCategory[]) => {
      const nextMemberOf = new Set<string>()
      all.forEach((category) => {
        if (category.is_default) return
        if (category.items.some((it) => it.id === placeIdString)) {
          nextMemberOf.add(category.id)
        }
      })
      setCategories(all.filter((c) => !c.is_default))
      setMemberOf(nextMemberOf)
    },
    [placeIdString]
  )

  /**
   * @param silent true면 로딩 표시를 띄우지 않는다.
   *   토글 후 재조회에까지 로딩을 걸면 목록이 통째로 "불러오는 중"으로 바뀌었다가
   *   다시 그려져 시트가 한 번 깜빡인다.
   */
  const load = useCallback(
    async (silent = false) => {
      // 캐시가 있으면 먼저 그린다 — 두 번째부터는 기다림 없이 바로 뜬다.
      // 단 토글 직후 재조회(silent)에는 쓰지 않는다. 캐시는 방금 바꾼 내용을
      // 아직 모르므로, 그대로 그리면 낙관적으로 바꿔둔 ✓가 한 번 되돌아간다.
      const cached = silent
        ? null
        : usePlaceBookmarkStore.getState().categories
      if (cached) {
        applyCategories(cached)
        setIsLoading(false)
      } else if (!silent) {
        setIsLoading(true)
      }

      try {
        const response = await bookmarkApi.getBookmarks()
        usePlaceBookmarkStore.getState().setCategories(response.categories)
        applyCategories(response.categories)
      } catch (error) {
        console.error('저장 상태를 불러오지 못했습니다:', error)
        if (!cached) setCategories([])
      } finally {
        setIsLoading(false)
      }
    },
    [applyCategories]
  )

  // 시트가 열렸다는 건 이 장소가 저장됐다는 뜻이다. 빈 아이콘을 눌렀으면 그 순간
  // 저장됐고, 채워진 아이콘을 눌렀으면 원래 저장돼 있었다. 그러니 서버 응답을
  // 기다릴 것 없이 처음부터 채워진 상태로 연다. 해제는 시트 안에서 한 번 더
  // 눌렀을 때만 일어난다.
  useEffect(() => {
    if (!isOpen) return
    setIsSaved(true)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    void load()
    // reloadToken: 시트를 연 뒤 백그라운드 저장이 끝나면 목록을 다시 읽는다.
  }, [isOpen, load, reloadToken])

  /** 리스트 한 곳에 넣거나 뺀다 */
  const handleToggleCategory = async (category: BookmarkCategory) => {
    if (pendingIds.has(category.id)) return

    const wasMember = memberOf.has(category.id)

    // 먼저 화면을 바꾸고 실패하면 되돌린다(서버 왕복이 수백 ms).
    setPendingIds((prev) => new Set(prev).add(category.id))
    setMemberOf((prev) => {
      const next = new Set(prev)
      if (wasMember) {
        next.delete(category.id)
      } else {
        next.add(category.id)
      }
      return next
    })

    try {
      await bookmarkApi.toggleBookmark(placeId, {
        bookmark_category_id: category.id,
      })
      // 담으면 내 저장이 된다. 빼는 경우엔 다른 리스트에 남아있을 수 있어 다시 읽는다.
      if (!wasMember) {
        setIsSaved(true)
        onSavedChange?.(true)
        // 담았다는 말은 시트가 닫힌 뒤에 한 번만 한다(PlatelistPickerSheet).
      } else {
        if (currentCategoryId && category.id === currentCategoryId) {
          onRemovedFromCurrentCategory?.()
        }
        await load(true)
      }
    } catch (error: any) {
      setMemberOf((prev) => {
        const next = new Set(prev)
        if (wasMember) {
          next.add(category.id)
        } else {
          next.delete(category.id)
        }
        return next
      })
      toastUtil.error(
        error.response?.data?.message || '저장 처리에 실패했습니다'
      )
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev)
        next.delete(category.id)
        return next
      })
    }
  }

  /**
   * 머리말 북마크.
   *  - 저장됨 → 내가 담은 모든 리스트에서 해제
   *  - 해제됨 → 다시 저장(리스트 미지정). 해제 후 되돌릴 길이 없으면 안 된다.
   */
  const handleUnsaveAll = async (
    requestClose: (delayMs?: number, toastAfter?: string) => void
  ) => {
    if (!isSaved) {
      setIsSaved(true)
      onSavedChange?.(true)
      try {
        await bookmarkApi.toggleBookmark(placeId, {})
        await load(true)
      } catch (error: any) {
        setIsSaved(false)
        onSavedChange?.(false)
        toastUtil.error(error.response?.data?.message || '저장에 실패했습니다')
      }
      return
    }

    // 리스트에 담긴 게 있을 때만 확인을 받는다. 아무 리스트에도 없으면
    // 잃을 게 없으므로 바로 해제한다.
    if (memberOf.size > 0) {
      const ok = await dialogUtil.confirm({
        message: '저장을 취소하면\n모든 플레이트리스트에서 삭제됩니다',
        confirmText: '저장 취소',
        cancelText: '취소',
      })
      if (!ok) return
    }

    // 화면을 먼저 바꾸고 서버는 뒤따르게 한다. 응답을 기다렸다 바꾸면
    // 누르고 나서 한 박자 늦게 반응하는 것처럼 보인다.
    const previousMemberOf = memberOf
    setIsSaved(false)
    // 확인창이 "모든 플레이트리스트에서 삭제됩니다"라고 약속했으므로 전부 지운다.
    // 서버도 내가 볼 수 있는 리스트 전부에서 지우므로 화면과 어긋나지 않는다.
    setMemberOf(new Set())
    onSavedChange?.(false)

    try {
      await bookmarkApi.removeSavedPlace(placeId)
      // 리스트에서 하나 빼는 건 체크가 풀리는 것으로 충분하지만, 이건 저장
      // 자체를 지우는 일이라 알린다 — 되돌릴 길이 없다.
      /*
       * 저장이 사라졌으니 시트에 남을 이유가 없다. 다만 곧바로 내려보내면
       * 북마크가 빈 아이콘으로 바뀌는 걸 못 보고 화면이 휙 사라진다.
       * 반 박자 두고 내려간다 — 리스트 탭(1초)보다는 짧다. 여기는 더
       * 누를 것이 없어 기다릴 이유가 없다.
       *
       * 알리는 것은 다 내려간 뒤다. 토스트가 화면 아래에서 올라오는데
       * 시트가 아직 거기 있으면 가려진다.
       */
      requestClose(UNSAVE_CLOSE_MS, '저장된 항목에서 삭제되었어요')
      await load(true)
    } catch (error: any) {
      setIsSaved(true)
      setMemberOf(previousMemberOf)
      onSavedChange?.(true)
      toastUtil.error(
        error.response?.data?.message || '저장 해제에 실패했습니다'
      )
    }
  }

  /*
   * 리스트를 새로 만들러 간다.
   *
   * 만들기는 바텀시트가 아니라 전체 페이지(/platelist/new)다. 떠나면 이 시트를
   * 띄운 화면이 통째로 내려갔다 돌아오므로, 어느 장소의 시트였는지 적어두고
   * 간다 — 돌아오면 BookmarkButton 이 그걸 보고 시트를 다시 연다.
   */
  const goCreate = () => {
    rememberBeforeCreate({ key: `place-save:${placeId}`, checked: [] })
    navigate('/platelist/new')
  }

  return (
    <PlatelistPickerSheet
      isOpen={isOpen}
      onClose={onClose}
      categories={categories}
      checkedIds={memberOf}
      isLoading={isLoading}
      onToggle={handleToggleCategory}
      onCreate={goCreate}
      header={({ requestClose }) => (
        <div className="w-full flex items-center px-20px pb-20px">
          <div className="flex-1 min-w-0">
            <div className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main truncate">
              {placeName || '이 장소'}
            </div>
            {placeAddress && (
              <div className="mt-4px font-pretendard font-regular text-14px leading-20px text-textColorText2 truncate">
                {shortenSido(placeAddress)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => void handleUnsaveAll(requestClose)}
            className="w-26px h-26px flex items-center justify-center shrink-0"
            aria-label={isSaved ? '저장 취소' : '저장'}
          >
            <img
              src={
                isSaved
                  ? '/images/bookmark-filled.png'
                  : '/images/bookmark-empty.png'
              }
              alt=""
              className="w-full h-full"
            />
          </button>
        </div>
      )}
    />
  )
}

export default PlaceSaveSheet
